/**
 * Phase 12 — a campaign group's texts ranked on the editor's LIVE ranking
 * settings (POST preview, zero writes): the formula and floor debounced so a
 * slider drag asks once, rows without a metric dropped, the previous answer
 * kept on screen while the next one loads (`pending` = an edit still waiting
 * out the debounce). Any campaign of the group answers the same (one tracker
 * link). The text rules (Phase 19) ride along: texts and prompt groups come
 * back with their hits.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  cleanHourFormula,
  type DropGenTextRankingConfig,
  type DropGenTextRankingPreview,
} from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../useDropGenerator';

export interface TextRankingArgs {
  countryKey: string;
  campaignWiserId: number | null;
  ranking: DropGenTextRankingConfig;
}

export function useTextRanking({ countryKey, campaignWiserId, ranking }: TextRankingArgs) {
  // Debounce ONE stable string: an object would be new on every render and
  // restart the debounce forever (a re-render every 400 ms while open).
  const liveKey = JSON.stringify({ formula: cleanHourFormula(ranking.formula), topN: ranking.topN, excluded: ranking.excluded, rules: ranking.rules ?? [] });
  const bodyKey = useDebouncedValue(liveKey, 400);
  const body = useMemo(() => JSON.parse(bodyKey) as Pick<DropGenTextRankingConfig, 'formula' | 'topN' | 'excluded' | 'rules'>, [bodyKey]);
  const query = useQuery({
    queryKey: [...dropGeneratorKeys.all, 'text-ranking', countryKey, campaignWiserId, bodyKey] as const,
    queryFn: async () => {
      const res = await api.post<{ data: DropGenTextRankingPreview }>(
        `/drop-generator/text-ranking/${countryKey}/${campaignWiserId}/preview`,
        body,
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey && campaignWiserId && body.formula.groups.length > 0),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
    // A first read of the tracker's texts runs on the server; ask again until it is in —
    // also from a background tab, so the ranking is there when the operator comes back.
    refetchInterval: (q) => ((q.state.data?.reading.length ?? 0) > 0 ? 1500 : false),
    refetchIntervalInBackground: true,
  });
  return { ...query, pending: liveKey !== bodyKey };
}
